(ns admin.components.composite.moment-editor.moments-list
  (:require [re-frame.core :as re-frame]
            [applied-science.js-interop :as j]
            [clojure.contrib.humanize :as humanize]
            [clojure.string :refer [capitalize]]
            [potpuri.core :as pot]

            [admin.events.moments :as >moments]
            [admin.subscriptions.moments :as <moments]
            [admin.components.composite.learning-objective-pill :as lo-pill]
            [admin.utils :as au]
            [kyber.components.composite.loading :as loading]
            [kyber.lib.pdftron.core :as pdftron-utils]
            [kyber.components.base.icons :as icon]
            [kyber.components.base.button :as button]
            [kyber.components.base.link :as link]
            [kyber.components.base.text :as text]
            [admin.components.composite.plo-uploader :as plo-uploader]
            [kyber.components.composite.resource :as rsrc]
            [kyber.styles.core :refer [gs styles->classes]]
            [kyber.utils :as ku]))

(def classes
  (styles->classes
   {:moment-list-container
    {:padding (gs [:spacing :p28])
     :width "100%"
     :overflow-y :scroll}

    :moment-card-container
    {:width "100%"
     :background-color (gs [:colors :true-white])
     :box-shadow (gs [:shadows :shadow-6])
     :border-radius (gs [:radius :r7])
     :position :relative

     "&:last-child"
     {:margin-bottom (gs [:spacing :p0])}}

    :panel-list-overlay
    {:position :absolute
     :top 0
     :left 0
     :z-index 1
     :width "100%"
     :height "100%"
     :background (gs [:colors :true-white])
     :border-radius (gs [:radius :r7])
     :opacity 0.80
     :display :flex
     :justify-content :center
     :align-items :center}

    :moment-card-header-type-container
    {:padding [(gs [:spacing :p4])
               (gs [:spacing :p12])]}

    :moment-card-header-preview-container
    {:display :flex
     :align-items :flex-start
     :justify-content :flex-start
     :width "100%"}

    :moment-cta-container
    {:display :flex
     :align-items :center
     :justify-content :space-between
     :width "100%"
     :padding (gs [:spacing :p12])}

    :moment-body-container
    {:flex-grow 1
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p12])]}

    :warning-banner
    {:border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :border-radius [(gs [:radius :r7])
                     (gs [:radius :r7])
                     (gs [:radius :r0])
                     (gs [:radius :r0])]
     :overflow :hidden
     :margin [(gs [:spacing :p0])
              (gs [:spacing :p0])
              (gs [:spacing :p2])]}

    :warning-banner-title
    {:color (gs [:colors :true-white])
     :background-color (gs [:colors :error-600])
     :margin (gs [:spacing :p0])
     :padding [(gs [:spacing :p4])
               (gs [:spacing :p8])]}

    :warning-message
    {:padding [(gs [:spacing :p1])
               (gs [:spacing :p8])]
     :margin (gs [:spacing :p0])}

    :success-icon
    {:color (gs [:colors :success-500])}

    :warning-icon
    {:color (gs [:colors :warning-500])}

    :error-icon
    {:color (gs [:colors :error-500])}

    :visibility-icon
    {:color (gs [:colors :gray-scale-500])}

    :step-row-container
    {:display :flex
     :align-items :center
     :justify-content :space-between
     :border-bottom (str "1px solid " (gs [:colors :gray-scale-100]))
     :padding [(gs [:spacing :p2])
               (gs [:spacing :p12])]}

    :step-row-label-container
    {:display :flex
     :align-items :center

     "& > *:first-child"
     {:margin-right (gs [:spacing :p8])}}

    :preview-container
    {:width "33.333%"
     :display :flex
     :justify-content :space-between
     :align-items :center
     :flex-shrink 0
     :padding [(gs [:spacing :p0])
               (gs [:spacing :p0])
               (gs [:spacing :p12])
               (gs [:spacing :p12])]}

    :icon-title
    {:word-break :break-all
     :margin-left (gs [:spacing :p12])}

    :list-pill
    {:background (gs [:colors :gray-scale-300])
     :color (gs [:colors :true-white])
     :font-weight (gs [:font-weight :body-30])
     :text-align :center
     :text-transform :uppercase
     :display :block
     :padding (gs [:spacing :p8])
     :border-radius "40px"
     :width "150px"
     :margin :auto}

    :list-track-segment
    {:text-align :center
     "&:after"
     {:content "\"\""
      :height (gs [:spacing :p32])
      :border-right (str "4px solid " (gs [:colors :gray-scale-300]))
      :display :block
      :width "50%"
      :margin-left "-2px"}}

    :adjust-time-container
    {:display :flex
     :align-items :center

     "& > *"
     {:margin-right (gs [:spacing :p12])

      "&:last-child"
      {:margin-right (gs [:spacing :p0])}}}}))

(defn- has-question?
  "Moment contains a question"
  [{:moment/keys [question]}]
  (some? question))

(defn- no-pdf-placement?
  "Moment has not been placed in pdf"
  [{:moment/keys [xfdf-id]}]
  (not (some? xfdf-id)))

(defn- no-video-placement?
  "Moment has not been placed in video"
  [{:moment/keys [display-at]}]
  (not (some? display-at)))

(defn- no-behavior?
  [{:moment/keys [behavior]}]
  (not (some? behavior)))

(defn- no-learning-objective? [{:moment/keys [learning-objective]}]
  (not (some? learning-objective)))

#_(defn- no-pdf-halos? [_moment]
    true)

(defn- no-video-halos? [{:moment/keys [start-time end-time]}]
  (not (and (some? start-time)
            (some? end-time))))

(defn- highlight-moment-fn
  "Highlights the moment card to match the selected annotation highlight"
  [{:keys [moment dispatch-local]}]
  (fn [_]
    (re-frame/dispatch [::>moments/set-active-moment-uuid (:moment/uuid moment)])
    (dispatch-local [:highlight-selected-pdf-moment (ku/nsmap->map moment)])))

(defn- edit-moment-fn
  "Creates a function to transition to the proper edit panel / state"
  [{:keys [moment-uuid panel dispatch-local]}]
  (fn [_]
    ;; Using a dispatch-sync because set-active-moment-uuid needs to complete before 
    ;; the dispatch-local is called or the editor will open the previously edited
    ;; moment rather than the currently selected one.
    (re-frame/dispatch-sync [::>moments/set-active-moment-uuid moment-uuid])
    (dispatch-local (cond-> [:enter-moment-edit-view]
                      (some? panel)
                      (conj panel)))))

(defn- moment-ctas [{:keys [moment dispatch-local editor-state]}]
  (let [{moment-uuid :moment/uuid
         :moment/keys [start-time end-time xfdf-id]} moment
        {:keys [activity-uuid curriculum-uuid video-activity?]} @editor-state
        is-deleting (re-frame/subscribe [:request/is-dispatching
                                         ::>moments/remove-moment])]
    [:div {:class (:moment-cta-container classes)}
     [:div {:style {:display :flex
                    :align-items :center}}
      [button/primary-button
       {:on-click (edit-moment-fn {:moment-uuid moment-uuid
                                   :dispatch-local dispatch-local})
        :style {:margin-right (gs [:spacing :p12])}}
       "Edit"]
      [link/secondary-anchor-button
       {:href (au/->learner-url {:curriculum curriculum-uuid
                                 :activity activity-uuid
                                 :query-string (if video-activity?
                                                 (str "?segs="
                                                      start-time
                                                      "&sege="
                                                      end-time
                                                      "&segm="
                                                      moment-uuid)
                                                 (str "?segm="
                                                      moment-uuid))})
        :target "_blank"}
       "Preview"]]

     [button/danger-button-link
      {:disabled @is-deleting
       :on-click
       #(re-frame/dispatch
         [:notifier/create-alert
          {:title "Remove Moment?"
           :message (str "Are you sure you want to remove this moment? "
                         "This action cannot be undone.")
           :confirm-text "Remove Moment"
           :confirm-action (fn []
                             (re-frame/dispatch
                              [::>moments/delete-moment
                               {:moment/uuid moment-uuid
                                :activity/uuid activity-uuid}
                               {:request-name (keyword :admin.events.moments
                                                       (str "remove-moment-" moment-uuid))
                                :on-success
                                (fn [_]
                                  (re-frame/dispatch
                                     [:pdftron-viewer/remove-annotation
                                      {:xfdf-id xfdf-id
                                       :viewer-key :activity-moment-tool}])
                                  (dispatch-local [:exit-moment-edit-view {:deleted? true}]))}]))
           :deny-text "Keep Moment"}])}
      "Remove"]]))

(defn- render-icon-preview
  [{:keys [icon on-icon-click]}]
  (if (some? on-icon-click)
    [button/icon-button-lg {:on-click identity} icon]
    [icon/icon-lg {} icon]))

(defn- video-preview
  [{:keys [resource activity-uuid time]}]
  [:div {:class (:preview-container classes)}
   [rsrc/resource
    {:tag :img
     :style {:border (str "1px solid " (gs [:colors :gray-scale-200]))}
     :src (ku/get-thumbnail-url
           {:activity-uuid activity-uuid
            :time time
            :resource-uuid (:resource/uuid resource)})
     :alt "Video Preview"}]])

(defn- pdf-preview
  [{:keys [resource]}]
  (let [{resource-name :resource/name} resource]
    [:div {:class (:preview-container classes)}
     [render-icon-preview {:icon :file-pdf}]
     [text/title-10 :p {:class (:icon-title classes)} resource-name]]))

(defn- audio-preview
  [{:keys [resource]}]
  (let [{resource-name :resource/name} resource]
    [:div {:class (:preview-container classes)}
     [render-icon-preview {:icon :file-audio}]
     [text/title-10 :p {:class (:icon-title classes)} resource-name]]))

(defn- image-preview
  [{:keys [resource]}]
  (let [{:resource/keys [url]} resource]
    [:div {:class (:preview-container classes)}
     [rsrc/resource
      {:tag :img
       :style {:border (str "1px solid " (gs [:colors :gray-scale-200]))}
       :src url
       :alt "Moment Preview Image"}]]))

(defn- highlighted-resource-preview
  [{:keys [resource activity-uuid]}]
  (let [{:resource/keys [file-type]} resource]
    (case (keyword file-type)
      :video [video-preview {:resource resource :activity-uuid activity-uuid}]
      :pdf [pdf-preview {:resource resource :activity-uuid activity-uuid}]
      :audio [audio-preview {:resource resource :activity-uuid activity-uuid}]
      :image [image-preview {:resource resource :activity-uuid activity-uuid}]
      [:div])))

(defn- moment-body-preview [{:keys [moment resources-lookup editor-state]}]
  (let [{:moment/keys [highlighted-resource display-at]} moment
        {:keys [activity-uuid plo-uuid]} @editor-state]
    (cond
      (some? highlighted-resource)
      [highlighted-resource-preview
       {:resource (get resources-lookup (:resource/uuid highlighted-resource))
        :activity-uuid activity-uuid}]

      (some? display-at)
      [video-preview {:activity-uuid activity-uuid
                      :resource {:resource/uuid plo-uuid}
                      :time display-at}])))

(defn- moment-body [{:keys [moment dispatch-local]}]
  (let [{moment-type :moment/type
         moment-uuid :moment/uuid
         :moment/keys [title body question]} moment]
    (when (some? moment-type)
      [:div {:class (:moment-body-container classes)}
       (if (= "question" moment-type)
         (if (nil? question)
           [:div
            [text/caption-20 :p
             {:style {:color (gs [:colors :error-500])}}
             "No question to display"]
            [button/button-link
             {:on-click (edit-moment-fn {:dispatch-local dispatch-local
                                         :moment-uuid moment-uuid
                                         :panel :details})}
             "Add question"]]
           [:div {:dangerouslySetInnerHTML
                  {:__html (-> question
                               :question/body
                               (humanize/truncate 250))}}])

         [:div
          (when (some? title)
            [text/title-10 :p title])

          (when (some? body)
            [text/body-20 :p body])])])))

(defn- warning-banner [{:keys [moment]}]
  (let [{:moment/keys [admin-message]} moment]
    (when (seq admin-message)
      [:div {:class (:warning-banner classes)}
       [text/title-10 :p {:class (:warning-banner-title classes)}
        [:<>
         [icon/icon-sm {:style {:color :inherit
                                :margin-right (gs [:spacing :p8])}}
          :exclamation-circle-solid]
         "Learners won’t be able to see this moment"]]
       [:ul {:style {:margin-top (gs [:spacing :p4])
                     :margin-bottom (gs [:spacing :p4])}}
        (for [message admin-message]
          [:li {:key message}
           [text/title-10 :p {:class [(:warning-message classes)]}
            (str "• " message)]])]])))

(defn- moment-card-header [{:keys [moment] :as params}]
  (let [{moment-type :moment/type} moment
        editor-state (params :editor-state)]
    [:<>
     [:div {:class [(:moment-card-header-type-container classes)]}
      (if (some? moment-type)
        [text/caption-30 :p (au/moment->type-display moment editor-state)]
        [text/caption-30 :p "No moment type set"])]

     [:div {:class [(:moment-card-header-preview-container classes)]}
      [moment-body-preview params]
      [moment-body params]]]))

(defn- success-icon
  []
  [icon/icon {:class (:success-icon classes)} :check-circle])

(defn- warning-icon
  []
  [icon/icon {:class (:warning-icon classes)} :exclamation-circle])

(defn- error-icon
  []
  [icon/icon {:class (:error-icon classes)} :times-circle])

(defn- visible-to-all-icon
  []
  [icon/icon {:class (:visibility-icon classes)} :friends-solid])

(defn- visible-by-tags-icon
  []
  [icon/icon {:class (:visibility-icon classes)} :friends])

(defn- step-row
  "One row in the moment steps list"
  [{:keys [status label component panel error-label dispatch-local moment-uuid]}]
  [:li {:class (:step-row-container classes)}
   [:div {:class (:step-row-label-container classes)}
    (case status
      :success [success-icon]
      :warning [warning-icon]
      :error [error-icon]
      :visible-to-all [visible-to-all-icon]
      :visible-by-tags [visible-by-tags-icon]
      (js/console.error "Invalid status"))
    [text/caption-30 :p label]]
   (if (get #{:error :warning :visible-to-all} status)
     [button/button-link
      {:on-click (edit-moment-fn {:dispatch-local dispatch-local
                                  :moment-uuid moment-uuid
                                  :panel panel})}
      error-label]
     (when (some? component) component))])

(defn- annotation
  "Renders the current moment annotation assignment"
  [{:keys [moment dispatch-local editor-state]}]
  (let [{:moment/keys [page-number xfdf-id] moment-uuid :moment/uuid}
        moment

        {:keys [apis]}
        (pdftron-utils/get-pdftron-viewer (:player-key @editor-state))

        has-no-placement
        (no-pdf-placement? moment)

        annotation-manager
        (:annotation-manager apis)

        annotation
        (when (some? xfdf-id)
          (j/call annotation-manager :getAnnotationById xfdf-id))

        [_ tool-label _]
        (when (some? annotation)
          (au/annotation->tool annotation))

        panel-id
        :placement]
    [step-row (cond-> {:status (if has-no-placement
                                 :error
                                 :success)
                       :label (if has-no-placement
                                "Placement & Display"
                                (str "Placement (" tool-label ")"))
                       :panel panel-id
                       :error-label "Add Placement"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (false? has-no-placement)
                (assoc :component [button/button-link
                                   {:on-click (highlight-moment-fn
                                               {:dispatch-local dispatch-local
                                                :moment moment})}
                                   (str "Page " (inc page-number))]))]))

(defn- learning-objective
  "Renders the current moment learning objective assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-uuid :moment/uuid
         learning-objective :moment/learning-objective}
        moment

        has-no-learning-objective
        (no-learning-objective? moment)]
    [step-row (cond-> {:status (if has-no-learning-objective
                                 :warning
                                 :success)
                       :label "Learning Objective"
                       :panel :learning-objective
                       :error-label "Add Learning Objective"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (some? learning-objective)
                (assoc :component [lo-pill/render
                                   {:label (:learning-objective/label
                                            learning-objective)}]))]))

(defn- moment-type
  "Renders the current moment type assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-type :moment/type
         moment-uuid :moment/uuid
         :moment/keys [question title]}
        moment

        panel-id :details
        has-type (some? moment-type)
        has-question (some? question)
        has-note (some? title)
        has-content (or (true? has-question)
                        (true? has-note))]
    [step-row (cond-> {:status (if (or (false? has-type)
                                       (false? has-content))
                                 :error
                                 :success)
                       :label (cond
                                (false? has-type)
                                "Moment Type"

                                (= moment-type "question")
                                "Type: Question"

                                (= moment-type "note")
                                "Type: Note")
                       :panel panel-id
                       :error-label (cond
                                      (false? has-type)
                                      "Select a moment type"

                                      (= moment-type "question")
                                      "Add a question"

                                      (= moment-type "note")
                                      "Add a note")
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (true? has-content)
                (assoc :component
                       [button/button-link
                        {:on-click (edit-moment-fn
                                    {:dispatch-local dispatch-local
                                     :moment-uuid moment-uuid
                                     :panel panel-id})}
                        (str "Edit " (capitalize moment-type))]))]))

(defn- behavior
  "Renders the current moment behavior assignment"
  [{:keys [moment dispatch-local]}]
  (let [has-no-behavior (no-behavior? moment)
        {moment-uuid :moment/uuid} moment
        panel-id :behavior]
    [step-row (cond-> {:status (if has-no-behavior
                                 :error
                                 :success)
                       :label "Behavior"
                       :panel panel-id
                       :error-label "Add Moment Behavior"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (false? has-no-behavior)
                (assoc :component
                       [:div {:class (:adjust-time-container classes)}
                        [text/caption-20 :p (case (keyword (:moment/behavior moment))
                                              :shout "Active (Opens moment & pauses)"
                                              :whisper "Expanded (Opens moment)"
                                              :none "Collapsed (Moment remains closed)"

                                              (js/console.error "Behavior not supported"))]
                        [button/button-link
                         {:on-click (edit-moment-fn
                                     {:dispatch-local dispatch-local
                                      :moment-uuid moment-uuid
                                      :panel panel-id})}
                         "Adjust"]]))]))

(defn- feedback
  "Renders the current question moment feedback assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-uuid :moment/uuid
         :moment/keys [question]} moment
        {:question/keys [educator-feedback]} question]
    [:<>
     (when (some? question)
       [step-row (cond-> {:status (if (nil? educator-feedback)
                                    :warning
                                    :success)
                          :label "Feedback"
                          :panel :details
                          :error-label "Add Feedback"
                          :moment-uuid moment-uuid
                          :dispatch-local dispatch-local}
                   (some? educator-feedback)
                   (assoc :component [button/button-link
                                      {:on-click (edit-moment-fn
                                                  {:dispatch-local dispatch-local
                                                   :moment-uuid moment-uuid
                                                   :panel :details})}
                                      "Edit Feedback"]))])]))

(defn- resources
  "Renders the current moment resource assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-uuid :moment/uuid
         resources :moment/resources} moment
        has-no-resources (empty? resources)
        resource-count (count resources)
        panel-id :resources]
    [step-row (cond-> {:status (if has-no-resources
                                 :warning
                                 :success)
                       :label "Resources"
                       :panel panel-id
                       :error-label "Add Resources"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (pos? resource-count)
                (assoc :component [button/button-link
                                   {:on-click (edit-moment-fn
                                               {:dispatch-local dispatch-local
                                                :moment-uuid moment-uuid
                                                :panel panel-id})}
                                   (cond-> (str resource-count " Resource")
                                     (> resource-count 1)
                                     (str "s"))]))]))

#_(defn- visibility
    "Renders the current moment's visibility assignment"
    [{:keys [moment activity-tags dispatch-local]}]
    (let [{moment-uuid :moment/uuid
           :moment/keys [valid-tags]} moment
          active-moment-tags (filterv #(seq (:content-provider.tag-group/values %)) valid-tags)
          tag-group-names (map #(:content-provider.tag-group/name %) activity-tags)
          panel-id :visibility]
      [step-row (cond-> {:status (if (empty? active-moment-tags)
                                   :visible-to-all
                                   :visible-by-tags)
                         :label (if (seq active-moment-tags)
                                  (str "Visible to some learners, based on their "
                                       (first tag-group-names)
                                       (when (second tag-group-names)
                                         (str " or " (second tag-group-names))))
                                  (str "Visible to all learners, regardless of their "
                                       (first tag-group-names)
                                       (when (second tag-group-names)
                                         (str " or " (second tag-group-names)))))
                         :panel panel-id
                         :error-label "Configure Visibility"
                         :moment-uuid moment-uuid
                         :dispatch-local dispatch-local}
                  (seq active-moment-tags)
                  (assoc :component [button/button-link
                                     {:on-click (edit-moment-fn
                                                 {:dispatch-local dispatch-local
                                                  :moment-uuid moment-uuid
                                                  :panel panel-id})}
                                     "Edit Visibility"]))]))

#_(defn- pdf-halos
    "Renders the current pdf moment halo assignment"
    [{:keys [moment dispatch-local]}]
    (let [has-no-halos (no-pdf-halos? moment)]
      [step-row {:status (if has-no-halos
                           :error
                           :success)
                 :label "Moment Halos"
                 :panel :halos
                 :error-label "Add Moment Halos"
                 :moment-uuid (:uuid moment)
                 :dispatch-local dispatch-local}]))

(defn- video-placement
  "Renders the current pdf moment halo assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-uuid :moment/uuid
         :moment/keys [display-at hide-at behavior]} moment
        panel-id :placement
        has-no-placement (no-video-placement? moment)
        fmt-time #(-> %
                      (ku/seconds->time-string)
                      (ku/fmt-time-string))]
    [step-row (cond-> {:status (if has-no-placement
                                 :warning
                                 :success)
                       :label "Moment Placement"
                       :panel panel-id
                       :error-label "Add Moment Placement"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (false? has-no-placement)
                (assoc :component
                       [:div {:class (:adjust-time-container classes)}
                        [text/caption-20 :p
                         (cond-> (str (fmt-time display-at))
                           (and (= "whisper" behavior)
                                (some? hide-at))
                           (str " - " (fmt-time hide-at)))]
                        [button/button-link
                         {:on-click (edit-moment-fn
                                     {:dispatch-local dispatch-local
                                      :moment-uuid moment-uuid
                                      :panel panel-id})}
                         "Adjust"]]))]))

(defn- video-halos
  "Renders the current pdf moment halo assignment"
  [{:keys [moment dispatch-local]}]
  (let [{moment-uuid :moment/uuid
         :moment/keys [start-time end-time]} moment
        has-no-halos (no-video-halos? moment)
        panel-id :halos
        fmt-time #(-> %
                      (ku/seconds->time-string)
                      (ku/fmt-time-string))]
    [step-row (cond-> {:status (if has-no-halos
                                 :warning
                                 :success)
                       :label "Moment Halo"
                       :panel panel-id
                       :error-label "Add Moment Halo"
                       :moment-uuid moment-uuid
                       :dispatch-local dispatch-local}
                (false? has-no-halos)
                (assoc :component
                       [:div {:class (:adjust-time-container classes)}
                        [text/caption-20 :p
                         (str (fmt-time start-time)
                              " - "
                              (fmt-time end-time))]
                        [button/button-link
                         {:on-click (edit-moment-fn
                                     {:dispatch-local dispatch-local
                                      :moment-uuid moment-uuid
                                      :panel panel-id})}
                         "Adjust"]]))]))

(defn- pdf-moment-steps [params]
  [:ul
   [moment-type params]
   [annotation params]
   [behavior params]
   [learning-objective params]
   (when (-> params :moment has-question?)
     [feedback params])
   [resources params]
   ;; TODO: This will be revisited once overlapping moments
   ;;       have been worked out
   #_(when (-> params :activity-tags seq)
     [visibility params])
   ;; TODO: This will go back in when pdf halos
   ;;       have been revisited
   #_[pdf-halos params]])

(defn- video-moment-steps [params]
  [:ul
   [moment-type params]
   [video-placement params]
   [learning-objective params]
   (when (-> params :moment has-question?)
     [feedback params])
   [resources params]
   [video-halos params]
   ;; TODO: This will be revisited once overlapping moments
   ;;       have been worked out
   #_(when (-> params :activity-tags seq)
       [visibility params])])

(defn- moment-card-steps [{:keys [editor-state] :as params}]
  (let [{:keys [pdf-activity? video-activity?]} @editor-state]
    (cond
      pdf-activity?
      [pdf-moment-steps params]

      video-activity?
      [video-moment-steps params]

      :else
      (js/console.error "Activity type not supported."))))

(defn- moments-list-card
  [{:moment/keys [uuid] :as moment} props]
  (let [is-deleting
        (re-frame/subscribe
         [:request/is-dispatching
          (keyword :admin.events.moments
                   (str "remove-moment-" uuid))])]
    [:<>
     [:li {:id (str "moment_card_" uuid)
           :class [(:moment-card-container classes)]}
      (when @is-deleting
        [:div {:class (:panel-list-overlay classes)}
         [loading/circles]])
      [warning-banner (assoc props :moment moment)]
      [moment-card-header (assoc props :moment moment)]
      [moment-card-steps (assoc props :moment moment)]
      [moment-ctas (assoc props :moment moment)]]
     [:li {:key (str "track-seg-" uuid)
           :class (:list-track-segment classes)}]]))

(defn- moments-list
  [{:keys [moments active-moment moment-list-ref! editor-state dispatch-local] :as props}]
  (let [{:keys [pdf-activity? video-activity? highlight-active-moment?]} @editor-state
        label (cond
                pdf-activity? "Document"
                video-activity? "Video"
                :else nil)]
    [:ul {:ref #(do
                  (reset! moment-list-ref! %)
                  (when (and (some? moment-list-ref!)
                             highlight-active-moment?)
                    ;;Highlight moment card without highlighting associated annotation
                    ;;when one does not exist or is not yet placed
                    (if (and pdf-activity? 
                             (some? (:moment/xfdf-id active-moment)))
                      (dispatch-local [:highlight-selected-pdf-moment (ku/nsmap->map active-moment)])
                      (dispatch-local [:highlight-selected-video-moment (ku/nsmap->map active-moment)]))))
          :class [(:moment-list-container classes)]}
     [:li [text/caption-20 :p
           {:class (:list-pill classes)}
           (str "Start of " label)]]
     [:li {:key "track-seg-start"
           :class (:list-track-segment classes)}]
     (for [moment moments]
       ^{:key (:moment/uuid moment)}
       [moments-list-card moment props])
     [:li [text/caption-20 :p
           {:class (:list-pill classes)}
           (str "End of " label)]]]))

(defn- add-new-moment-button
  [{:keys [plo dispatch-local activity-uuid curriculum-uuid player-key
           timeline-state player-attributes video-activity? plo-type]}]
  (let [{:keys [time duration]}
        player-attributes]
    (when (and (= "complete" (:resource/status plo))
               (or (not video-activity?)
                   (nil? (:adding-moment-at @timeline-state))))
      [button/primary-button
       {:on-click (fn [_]
                    (if video-activity?
                      (do
                        (re-frame/dispatch [:media-player/pause-media
                                            {:player-key player-key}])
                        (re-frame/dispatch [::>moments/unset-active-moment-uuid])
                        (swap! timeline-state
                               assoc
                               :adding-moment-at
                               (ku/calc-percent duration time)))
                      (do
                        ;; This is a dispatch-sync because we need the active moment unset before
                        ;; we run the dispatch-local. Without this, the PDF scrolls to the most
                        ;; recent active moment when the user hits the " + Add New Moment" button.
                        ;; More details in ticket 10160.
                        (re-frame/dispatch-sync [::>moments/unset-active-moment-uuid])
                        (re-frame/dispatch
                         [::>moments/create-moment
                          {:moment/activity {:activity/uuid activity-uuid}
                           :moment/curriculum {:curriculum/uuid curriculum-uuid}
                           :moment/behavior "whisper"}
                          {:response-keys (>moments/get-response-keys plo-type)
                           :on-success #(re-frame/dispatch [::>moments/set-active-moment-uuid
                                                            (:moment/uuid %)])}])
                        (dispatch-local [:enter-moment-edit-view :placement]))))

        :style {:padding (gs [:spacing :p24])
                :width "100%"}}
       [:div {:style {:display :flex
                      :align-items :center}}
        [icon/icon {:style {:color :inherit
                            :margin-right (gs [:spacing :p12])}} :plus]
        (str "Add New Moment"
             (when (some? timeline-state)
               (str " at " (-> time
                               (ku/seconds->time-string true)
                               (ku/fmt-time-string)))))]])))

(defn- no-moments
  [{:keys [activity plo curriculum-uuid resources]
    :as add-moment-props}]
  [:div {:style {:display :flex
                 :flex-direction :column
                 :height "100%"
                 :justify-content :space-around
                 :align-items :center
                 :text-align :center}}
   [:div {:style {:padding (gs [:spacing :p48])}}
    [text/title-30 :p {}
     (str "Learning Stream")]
    [text/body-20 :span {}
     (str "There are currently no learning moments created for this "
          "activity. Once created, they will show up here!")]
    [:div {:style {:margin-top (gs [:spacing :p24])}}
     (cond
       (= "complete" (:resource/status plo))
       [add-new-moment-button add-moment-props]

       (nil? plo)
       [:<>
        [text/caption-30 :p
         {:style {:margin-bottom (gs [:spacing :p12])}}
         "Add a Primary Learning Object before creating learning moments."]
        [plo-uploader/render
         {:activity @activity
          :activity-resources resources
          :curriculum-uuid curriculum-uuid}]]

       :else
       [text/caption-30 :p {}
        "Learning moments can be created when your Primary Learning Object is ready."])]]])

(defn render [{:keys [activity dispatch-local editor-state timeline-state]}]
  (let [{:keys [pdf-activity? video-activity? player-key
                activity-uuid curriculum-uuid moment-list-ref!]}
        @editor-state

        annotation-layer-loaded
        (if (true? pdf-activity?)
          (re-frame/subscribe
           [:pdftron-viewer/get-annotation-layer-loaded
            {:viewer-key player-key}])
          (atom false))

        player-attributes
        (re-frame/subscribe
         [:media-player/player-attributes player-key])]
    (fn [_]
      (let [{:activity/keys [primary-learning-object plo-type moments
                             resources learning-objectives valid-tags]}
            @activity

            active-moment
            @(re-frame/subscribe [::<moments/active-moment])

            active-activity-tags (filterv #(seq (:content-provider.tag-group/values %)) valid-tags)

            add-moment-props
            {:activity-uuid activity-uuid
             :curriculum-uuid curriculum-uuid
             :dispatch-local dispatch-local
             :timeline-state timeline-state
             :video-activity? video-activity?
             :player-key player-key
             :resources resources
             :activity activity
             :plo primary-learning-object
             :plo-type plo-type
             :player-attributes @player-attributes}]
        (cond
          (and (= "complete" (:resource/status primary-learning-object))
               (true? pdf-activity?)
               (false? (boolean @annotation-layer-loaded)))
          [:div {:style {:position :absolute
                         :left "50%"
                         :top "50%"
                         :margin-top "-30px"}}
           [loading/circles]]

          (seq moments)
          [:div {:style {:display :flex
                         :flex-direction :column
                         :height "100%"
                         :width "100%"
                         :justify-content :space-between}}
           [moments-list
            {:activity-uuid activity-uuid
             :activity-tags active-activity-tags
             :dispatch-local dispatch-local
             :active-moment active-moment
             :moment-list-ref! moment-list-ref!
             :moments (sort-by (if video-activity? :moment/display-at :moment/page-number) moments)
             :learning-objective-lookup (pot/index-by :learning-objective/uuid
                                                    learning-objectives)
             :resources-lookup (pot/index-by :resource/uuid
                                           resources)
             :editor-state editor-state}]
           [add-new-moment-button add-moment-props]]

          :else
          [no-moments add-moment-props])))))
